import { AddressZero } from '@ethersproject/constants';
import moment from 'moment';
import React, { memo, useContext, useMemo } from 'react';
import { SpoolDto } from '../../../../types/spool-local';
import modalContext from '../../../../context/modal/modalContext';
import appContext from '../../../../context/app/appContext';
import Detail from '../../../Detail';
import CopyPopUp from '../../../CopyPopUp';
import { formatAccount, formatPercentages } from '../../../../helpers/formats';
import InfoPopUp from '../../../InfoPopUp';
import IncentivizeReward from '../../../../modals/infoPopUp/IncentivizeReward';
import { IncentivizedArrowIcon } from '../../../../assets/icons';
import AdjustedApy from '../../../../modals/infoPopUp/AdjustedApy';
import ExternalLink from '../../../ExternalLink';
import { config } from '../../../../config/config';
import { getRiskScore, mapRiskToRange } from '../../../../helpers/sdkHelper';
import { toBigNumber } from '../../../../web3/utils';
import { SupportedChains } from '../../../../config/constants';
import { useWeb3React } from '@web3-react/core';

type Props = {
    spoolDetails: SpoolDto;
};

const GeneralDetails: React.FC<Props> = ({ spoolDetails }) => {
    const {
        name,
        owner,
        address,
        tvr,
        riskModelAddress,
        performanceFees,
        riskTolerance,
        dateCreated,
        incentivizedRewards,
        baseApy,
        strategies,
        adjustedApy,
    } = spoolDetails;

    const { openModal } = useContext(modalContext);
    const { chainStrategies, chainRiskModels } = useContext(appContext);
    const { chainId } = useWeb3React();
    const { setCurrentViewingModel } = useContext(appContext);

    const handleRiskModelClick = () => {
        setCurrentViewingModel(riskModelAddress);
        openModal('riskModelDetails');
    };

    const filteredIncentivizedRewards = useMemo(
        () =>
            incentivizedRewards.filter(
                ({ isRemoved, rewardRate }) => !isRemoved && rewardRate !== '0'
            ),
        [incentivizedRewards]
    );

    return (
        <div className='general-details'>
            <Detail
                className='owner-address'
                title='Spool Owner Address'
                value={
                    <>
                        <span>{formatAccount(owner || AddressZero)}</span>
                        <CopyPopUp text={owner || AddressZero} />
                    </>
                }
            />
            <Detail
                className='risk-model'
                title='Risk Model'
                value={
                    <div onClick={handleRiskModelClick}>
                        {chainRiskModels[riskModelAddress]?.name || 'Model'}
                    </div>
                }
            />
            <Detail
                className='strategies'
                title='Strategies'
                value={strategies.map(({ id, address: strategyAddress }) => (
                    <InfoPopUp
                        className='strategy'
                        triggerMargin={{ x: 0, y: 3 }}
                        key={id}
                        trigger={
                            <span>
                                {chainStrategies[strategyAddress]?.name || 'Strategy'}
                            </span>
                        }
                    >
                        {chainStrategies[strategyAddress]?.detail || 'Strategy Detail'}
                    </InfoPopUp>
                ))}
            />

            <Detail
                title={`Spool Creator's Performance Fee`}
                value={`${(+performanceFees.owner).toFixed(2) || '0.00'}%`}
            />
            <Detail
                title={`Risk Model's Performance Fee`}
                value={`${(+performanceFees.riskProvider).toFixed(2) || '0.00'}%`}
            />
            <Detail
                className='incentivized-rewards'
                title={
                    <>
                        <span>Incentivized Rewards</span>
                        <IncentivizeReward
                            incentivizedRewards={filteredIncentivizedRewards}
                        />
                    </>
                }
                value={
                    <>
                        {filteredIncentivizedRewards?.length
                            ? incentivizedRewards
                                  .filter(({ isRemoved }) => !isRemoved)
                                  .map(({ symbol, apy, id }) => (
                                      <div key={id} className='reward'>
                                          <div className='token'>
                                              <span>{symbol || 'ASSET'}</span>
                                              <span className='apy'>
                                                  {formatPercentages(
                                                      parseFloat(apy) * 100 || 0
                                                  )}
                                                  % APY
                                              </span>
                                          </div>
                                          <IncentivizedArrowIcon incentivized />
                                      </div>
                                  ))
                            : 'N/A'}
                    </>
                }
            />
            <Detail
                title={`Spool DAO's Performance Fee`}
                value={`${
                    (+performanceFees.ecosystem + +performanceFees.treasury).toFixed(2) ||
                    '0.00'
                }%`}
            />
            <Detail
                className='risk-tolerance'
                title='Risk Appetite'
                value={
                    <>
                        <span>{getRiskScore(+riskTolerance)}</span> (
                        {mapRiskToRange(+riskTolerance) || '0'})
                    </>
                }
            />
            <Detail
                title='Stablecoin APY'
                value={`${formatPercentages(parseFloat(baseApy) * 100 || 0)}%`}
            />
            <Detail
                className='total-performance-fee'
                title={
                    <>
                        <span>Total Performance Fee</span>
                        <InfoPopUp>
                            <span>
                                Fees are based on the user’s profits earned through the
                                Spool and are distributed on withdrawal.
                            </span>
                        </InfoPopUp>
                    </>
                }
                value={`${
                    Object.values(performanceFees)
                        .reduce((totalFee, fee) => (totalFee += +fee), 0)
                        .toFixed(2) || '0.00'
                }%`}
            />
            <Detail
                title='Date Created:'
                value={moment.unix(+dateCreated).format('YYYY MMMM DD')}
            />
            <Detail
                className='adjusted-apy-after-fees'
                title={
                    <>
                        <span>
                            Adjusted APY After Performance Fees and Incentivized Rewards
                        </span>
                        <AdjustedApy
                            adjustedApy={adjustedApy}
                            baseApy={baseApy}
                            incentivizedRewards={incentivizedRewards}
                            performanceFees={performanceFees}
                        />
                    </>
                }
                value={`${formatPercentages(parseFloat(adjustedApy) * 100 || 0)}%`}
            />
            <p>
                Use the <ExternalLink to={config.urls.sdk}>SDK</ExternalLink> to connect
                this Spool to your website or
                {toBigNumber(tvr).lt(toBigNumber('100000')) && (
                    <>
                        <br />
                        <br />A Smart Vault always auto-compounds APY and with TVR &gt;
                        {chainId === SupportedChains.MAINNET ? '$100k' : '$1k'} also
                        reallocates funds when risk scores change.
                    </>
                )}
            </p>
            <div className='urls'>
                <div className='url'>
                    <p>
                        Spool URL :{' '}
                        <ExternalLink
                            to={`${config.urls.spoolApp}/${
                                config.urls.spoolPath
                            }/${address}${
                                chainId === SupportedChains.ARBITRUM_ONE
                                    ? '?network=arbitrum'
                                    : ''
                            }`}
                        >
                            {name}
                        </ExternalLink>
                    </p>
                    <CopyPopUp
                        text={`${config.urls.spoolApp}/${
                            config.urls.spoolPath
                        }/${address}${
                            chainId === SupportedChains.ARBITRUM_ONE
                                ? '?network=arbitrum'
                                : ''
                        }`}
                    />
                </div>
                <div className='url'>
                    <p>
                        {`${
                            chainId !== SupportedChains.ARBITRUM_ONE
                                ? 'Etherscan'
                                : 'Arbiscan'
                        } URL: `}
                        <ExternalLink
                            to={`${
                                chainId !== SupportedChains.ARBITRUM_ONE
                                    ? config.urls.etherscan
                                    : config.urls.arbiscan
                            }/address/${address}`}
                        >
                            {chainId !== SupportedChains.ARBITRUM_ONE
                                ? config.urls.etherscan
                                : config.urls.arbiscan}
                            /address/{address}
                        </ExternalLink>
                    </p>
                    <CopyPopUp
                        text={`${
                            chainId !== SupportedChains.ARBITRUM_ONE
                                ? config.urls.etherscan
                                : config.urls.arbiscan
                        }/address/${address}`}
                    />
                </div>
            </div>
        </div>
    );
};

export default memo(GeneralDetails);
